import type {
  ChartConfig,
  ChartDataModel,
  IChartPlugin,
  RenderContext
} from '@chartforge/shared-types';
import { renderWatermark } from './watermark';

/** Thrown by {@link renderChart} when a plugin rejects the data model. */
export class ChartValidationError extends Error {
  constructor(readonly errors: string[]) {
    super(`Invalid chart data: ${errors.join('; ')}`);
    this.name = 'ChartValidationError';
  }
}

const resizeCleanups = new WeakMap<HTMLElement, () => void>();

/**
 * The single render pipeline used by every shell (demo, Excel, Sheets,
 * Power BI adapters):
 *
 *   validate model → plugin.render → engine watermark → responsive resize.
 *
 * Resize handling lives here (rather than in each chart) so that re-renders
 * always re-run the full pipeline, including the entitlement watermark.
 */
export function renderChart(
  plugin: IChartPlugin,
  model: ChartDataModel,
  config: ChartConfig,
  ctx: RenderContext
): void {
  const result = plugin.validate(model);
  if (!result.ok) {
    throw new ChartValidationError(result.errors ?? ['unknown validation error']);
  }

  const draw = (drawCtx: RenderContext): void => {
    plugin.render(model, config, drawCtx);
    renderWatermark(drawCtx);
  };

  draw(ctx);
  attachResponsiveResize(ctx, draw);
}

function attachResponsiveResize(baseCtx: RenderContext, draw: (ctx: RenderContext) => void): void {
  const container = baseCtx.container;
  resizeCleanups.get(container)?.();
  resizeCleanups.delete(container);

  if (typeof ResizeObserver === 'undefined') {
    return;
  }

  let lastWidth = container.clientWidth;
  let lastHeight = container.clientHeight;

  const observer = new ResizeObserver(() => {
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height || (width === lastWidth && height === lastHeight)) {
      return;
    }
    lastWidth = width;
    lastHeight = height;
    // Re-run the FULL pipeline with the original context (watermark included)
    // so entitlement visuals survive resizes.
    draw({ ...baseCtx, width, height });
  });
  observer.observe(container);
  resizeCleanups.set(container, () => observer.disconnect());
}
